import type {ValidationRegistrar} from 'modules/VendorRegister/types';

import {CloseOutlined, InfoCircleOutlined} from '@ant-design/icons';
import {Button, Loading} from '@ds/components';
import {toast} from '@Toast';
import nationalCardExample from 'assets/images/vendor-reg/nationalCard.png';
import uploadPlaceholder from 'assets/images/vendor-reg/uploadFile.png';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useAppDispatch} from 'store';
import {sendDocument, uploadFile} from 'store/slices/vendor-registration/thunk';

type UploadedDocument = {
  id: string | number;
  fileKey: string;
  fileName: string;
  previewUrl: string;
};

type NationalCardProps = {
  registerValidation: ValidationRegistrar;
  onValidityChange: (isValid: boolean) => void;
};

const NationalCard: React.FC<NationalCardProps> = ({registerValidation, onValidityChange}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [document, setDocument] = useState<UploadedDocument | null>(null);
  const [uploading, setUploading] = useState(false);

  const validateStep = useCallback(async () => {
    if (!document?.id) {
      toast('لطفا عکس کارت ملی خود را بارگذاری کنید.');
      return false;
    }

    try {
      const response = await dispatch(sendDocument({nationalCard: document.id})).unwrap();

      if (response.isSuccess) {
        return true;
      }

      toast(response.message || 'خطا');
    } catch (error) {
      toast(
        typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof error.message === 'string'
          ? error.message
          : 'خطا'
      );
    }

    return false;
  }, [dispatch, document]);

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  useEffect(() => {
    return () => {
      if (document?.previewUrl) {
        URL.revokeObjectURL(document.previewUrl);
      }
    };
  }, [document]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      toast('حداکثر حجم مجاز ۲ مگابایت است.');
      event.target.value = '';
      return;
    }

    if (!['image/png', 'image/jpeg'].includes(selectedFile.type)) {
      toast('فقط فایل‌های PNG و JPEG مجاز هستند.');
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    setUploading(true);

    try {
      const response = await dispatch(uploadFile(formData)).unwrap();

      if (!response.isSuccess) {
        toast(response.message || 'خطا');
        return;
      }

      setDocument((currentDocument) => {
        if (currentDocument?.previewUrl) {
          URL.revokeObjectURL(currentDocument.previewUrl);
        }

        return {
          id: response.data.id,
          fileKey: response.data.file_key ?? String(response.data.id),
          fileName: response.data.file_name ?? selectedFile.name,
          previewUrl: URL.createObjectURL(selectedFile)
        };
      });
      onValidityChange(true);
    } catch (error) {
      toast(
        typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof error.message === 'string'
          ? error.message
          : 'خطا در بارگذاری فایل.'
      );
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const removeDocument = () => {
    setDocument((currentDocument) => {
      if (currentDocument?.previewUrl) {
        URL.revokeObjectURL(currentDocument.previewUrl);
      }

      return null;
    });
    onValidityChange(false);
  };

  return (
    <div className="flex flex-col gap-4 px-5 py-2.5">
      <div className="mt-5 flex flex-col gap-1.5">
        <p className="text-body-sm-emphasis text-neutral-fg">
          یک عکس واضح و خوانا از کارت ملی خود بارگذاری کنید.
        </p>
        <button
          type="button"
          className="relative flex aspect-13/5 cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-11"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          aria-label="انتخاب عکس کارت ملی"
        >
          <img
            className="size-full object-cover"
            src={document?.previewUrl ?? uploadPlaceholder}
            alt={document ? 'پیش‌نمایش کارت ملی' : ''}
          />
          {uploading ? (
            <span className="absolute inset-0 flex items-center justify-center bg-black/40">
              <span className="size-12">
                <Loading size="medium" color="inverse" />
              </span>
            </span>
          ) : null}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={handleFileChange}
        />
        {document ? (
          <div
            className="flex items-center gap-2 rounded-md border border-gray-03 bg-gray-01 p-2"
            key={document.fileKey}
          >
            <img className="size-10 rounded object-cover" src={document.previewUrl} alt="" />
            <span className="min-w-0 flex-1 truncate text-body-xs text-gray-08">
              {document.fileName}
            </span>
            <Button
              variant="ghost"
              color="secondary"
              size="sm"
              className="size-8 px-0!"
              onClick={removeDocument}
              aria-label="حذف عکس کارت ملی"
            >
              <CloseOutlined aria-hidden />
            </Button>
          </div>
        ) : null}
        <div className="flex items-center gap-1 text-body-xs text-gray-34">
          <InfoCircleOutlined aria-hidden className="shrink-0" />
          <span>حجم عکس حتما باید کمتر از 2mb و در فرمت‌های PNG و JPEG باشد</span>
        </div>
      </div>
      <div className="flex justify-center pt-2">
        <img src={nationalCardExample} alt="نمونه تصویر صحیح کارت ملی" />
      </div>
    </div>
  );
};

export default NationalCard;
